// Central source of truth for all plan pricing, limits, and display metadata.
// Update prices here, they propagate everywhere automatically.
// Price/limit NUMBERS can be overridden by admins via /admin/pricing
// (site_config.plan_pricing) — see pricing.ts. Structure lives here.

// `features` is the SINGLE canonical marketing copy for each plan — both
// /pricing and /checkout import this instead of keeping their own literal
// arrays, so the two pages can never drift out of sync with each other again
// (they did: checkout was missing bullets pricing had, and vice versa).
// Every line here must correspond to something actually built and gated —
// three lines were removed for exactly the opposite reason ("Priority
// support", "Enhanced agent profile", "Diaspora buyer spotlight" as a
// Market-Leader-exclusive): none of the three have ANY backing code. Do not
// re-add a feature bullet without first confirming it is real.
export const PLAN_CONFIG = {
  FSBO: {
    name: "FSBO",
    displayName: "FSBO (Landlord)",
    listingFee: 5,          // USD per listing (60 days)
    monthlyPrice: null as number | null,
    yearlyPrice: null as number | null,
    listingLimit: 1,
    photoLimit: 10,
    featuredCreditsPerMonth: 0,
    agentSeats: 0,
    features: ["1 active listing", "10 photos", "KYC verification", "Buyer messaging", "Lead inbox"],
  },
  // NOTE: enum value stays AGENT_SOLO for existing rows; the product is "Agent Starter".
  AGENT_SOLO: {
    name: "Agent Starter",
    displayName: "Agent Starter",
    listingFee: null as number | null,
    monthlyPrice: 40 as number | null,
    yearlyPrice: 32 as number | null,   // 20% off billed annually
    listingLimit: 10,
    photoLimit: 30,
    featuredCreditsPerMonth: 1,
    agentSeats: 0,
    features: ["10 active listings", "30 photos per listing", "Verified Agent badge", "Listing analytics", "1 featured credit/mo", "WhatsApp share cards"],
  },
  AGENT_PRO: {
    name: "Agent Pro",
    displayName: "Agent Pro",
    listingFee: null as number | null,
    monthlyPrice: 65 as number | null,
    yearlyPrice: 52 as number | null,
    listingLimit: 30,
    photoLimit: 40,
    featuredCreditsPerMonth: 2,
    agentSeats: 0,
    features: ["30 active listings", "40 photos per listing", "2 featured credits/mo", "Monthly performance report", "Everything in Starter"],
  },
  // NOTE: enum value stays AGENCY for existing rows; the product is "Agency 50".
  AGENCY: {
    name: "Agency 50",
    displayName: "Agency 50",
    listingFee: null as number | null,
    monthlyPrice: 120 as number | null,
    yearlyPrice: 96 as number | null,
    listingLimit: 50,
    photoLimit: 50,
    featuredCreditsPerMonth: 4,
    agentSeats: 5,
    features: ["50 active listings shared across your team", "5 agent seats", "4 featured credits/mo", "Branded agency page", "Team management"],
  },
  AGENCY_100: {
    name: "Agency 100",
    displayName: "Agency 100",
    listingFee: null as number | null,
    monthlyPrice: 180 as number | null,
    yearlyPrice: 144 as number | null,
    listingLimit: 100,
    photoLimit: 50,
    featuredCreditsPerMonth: 6,
    agentSeats: 10,
    features: ["100 active listings shared across your team", "10 agent seats", "6 featured credits/mo", "Quarterly market report", "Everything in Agency 50"],
  },
  MARKET_LEADER: {
    name: "Market Leader",
    displayName: "Market Leader",
    listingFee: null as number | null,
    monthlyPrice: 350 as number | null,
    yearlyPrice: 280 as number | null,
    listingLimit: 250,
    photoLimit: 60,
    featuredCreditsPerMonth: 10,
    agentSeats: 20,
    features: ["250 active listings shared across your team", "20 agent seats", "10 featured credits/mo", "Monthly market intelligence", "API access", "Everything in Agency 100"],
  },
  DEVELOPER: {
    name: "Developer",
    displayName: "Developer",
    listingFee: null as number | null,
    monthlyPrice: 60 as number | null,  // per ACTIVE PROJECT per month
    yearlyPrice: 48 as number | null,
    listingLimit: 1,                    // interpreted as projects; scaled by projectQuantity
    photoLimit: 50,
    featuredCreditsPerMonth: 0,
    agentSeats: 0,
    features: ["Dedicated project page", "Phase / unit tracker", "Verified Developer badge", "Monthly progress posts", "Buyer waitlist tool"],
  },
} as const

export type PlanKey = keyof typeof PLAN_CONFIG

export const PLAN_KEYS = Object.keys(PLAN_CONFIG) as PlanKey[]

/** Recurring (subscription) plans, in display order for the pricing page. */
export const SUBSCRIPTION_PLAN_ORDER: PlanKey[] =
  ["AGENT_SOLO", "AGENT_PRO", "AGENCY", "AGENCY_100", "MARKET_LEADER", "DEVELOPER"]

/** Plans that confer agency features (branded page, seats, invites). */
export const AGENCY_TIER_PLANS: readonly string[] = ["AGENCY", "AGENCY_100", "MARKET_LEADER"]

// ── Featured / visibility products ─────────────────────────────────────────────
export const FEATURED_BOOST_PRICE      = 15  // USD, 7-day featured window
export const FEATURED_BOOST_30D_PRICE  = 25  // USD, 30-day featured window
export const SPOTLIGHT_PRICE           = 10  // USD, 24h homepage spotlight
export const SPOTLIGHT_MAX_CONCURRENT  = 4   // paid slots live at once (scarcity is the product)
export const BUYER_BLAST_PRICE         = 40  // USD, one alert to all matched saved-search buyers

// ── Recurring add-ons (billed with the subscription renewal) ──────────────────
export const EXTRA_SEAT_PRICE          = 5   // USD/mo per extra agency seat
export const EXTRA_LISTINGS_BLOCK_SIZE = 10  // listings per purchased block
export const EXTRA_LISTINGS_BLOCK_PRICE = 10 // USD/mo per block of 10 listings

// ── Developer multi-project pricing ───────────────────────────────────────────
// $60/project; 3+ projects drop to $50/project ("3-pack $150").
export const DEVELOPER_BUNDLE_MIN      = 3
export const DEVELOPER_BUNDLE_RATE     = 50  // USD/project/mo at 3+ projects

export const LISTING_60DAY_DURATION = 60 // days a FSBO listing stays live

/**
 * Absolute upload ceiling, a runaway-loop backstop only. It MUST stay at or
 * above the largest `photoLimit` of any plan, otherwise it silently overrides
 * the band a customer paid for. (It was 30 while Market Leader advertised 60.)
 */
export const MAX_PHOTOS_ABSOLUTE = 100

// Free baseline a seller keeps AFTER their free trial expires (before paying):
// this many LIVE listings stay up; the rest pause until they subscribe.
export const FREE_TIER_LIMIT = 1

/** Quarterly-and-up discount: applies from 3 months to 11 months (before the
 *  yearly rate takes over at 12+). Must stay ≤ the 12-month discount or paying
 *  for two consecutive quarters would undercut a single longer term. */
export const QUARTERLY_DISCOUNT = 0.15

/**
 * Per-month rate for a subscription over N months, applying the tiered discounts
 * shown at checkout: 12+ months → the yearly rate (20% off); 3–11 months → 15% off
 * the monthly rate (covers the quarterly option and everything up to it);
 * otherwise the standard monthly rate. Shared by the server charge path and the
 * client so the displayed total and the amount billed always agree.
 */
export function subscriptionRate(monthly: number, yearly: number | null, months: number): number {
  if (months >= 12) return yearly ?? monthly
  if (months >= 3)  return parseFloat((monthly * (1 - QUARTERLY_DISCOUNT)).toFixed(2))
  return monthly
}

/** Price for a given plan + period combination (code defaults — server charges use pricing.ts). */
export function planPrice(plan: PlanKey, months: number): number {
  const config = PLAN_CONFIG[plan]
  if (!config.monthlyPrice) return 0
  return parseFloat((subscriptionRate(config.monthlyPrice, config.yearlyPrice, months) * months).toFixed(2))
}

/**
 * Developer monthly rate for a given number of active projects: per-project price,
 * with the bundle rate from DEVELOPER_BUNDLE_MIN projects up. qty is clamped to ≥1.
 */
export function developerMonthlyRate(perProject: number, qty: number): number {
  const q = Math.max(1, Math.floor(qty))
  const rate = q >= DEVELOPER_BUNDLE_MIN ? DEVELOPER_BUNDLE_RATE : perProject
  return parseFloat((rate * q).toFixed(2))
}

/**
 * Effective listing limit for a subscription row: the plan's band plus any
 * purchased +10 blocks. DEVELOPER is a per-project count scaled by quantity.
 */
export function effectiveListingLimit(
  plan: PlanKey | string | null,
  extraListingBlocks = 0,
  projectQuantity = 1,
): number {
  const config = PLAN_CONFIG[(plan ?? "FSBO") as PlanKey]
  if (!config) return FREE_TIER_LIMIT
  if ((plan as PlanKey) === "DEVELOPER") return Math.max(1, projectQuantity)
  return config.listingLimit + Math.max(0, extraListingBlocks) * EXTRA_LISTINGS_BLOCK_SIZE
}

/** Effective seat count for an agency-tier subscription: plan seats + purchased extras. */
export function effectiveSeatLimit(plan: PlanKey | string | null, extraSeats = 0): number {
  const config = PLAN_CONFIG[(plan ?? "FSBO") as PlanKey]
  if (!config) return 0
  return config.agentSeats + Math.max(0, extraSeats)
}
